import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Tabs, 
  Card, 
  Typography, 
  Space, 
  Tag, 
  Button, 
  Input, 
  List,
  Divider,
  Badge
} from 'antd';
import { 
  SearchOutlined, 
  CopyOutlined, 
  QuestionCircleOutlined
} from '@ant-design/icons';
import { CommandRegistry } from '../../services/commands/CommandRegistry';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface CommandHelpProps {
  visible: boolean;
  onClose: () => void;
  selectedCommand?: string;
}

export const CommandHelp: React.FC<CommandHelpProps> = ({
  visible,
  onClose,
  selectedCommand,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCommands, setFilteredCommands] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  
  const commandRegistry = CommandRegistry.getInstance();

  useEffect(() => {
    if (selectedCommand) {
      setActiveTab('all');
      setSearchQuery(selectedCommand);
    }
  }, [selectedCommand]);

  useEffect(() => {
    const commands = commandRegistry.getAllCommands();
    
    if (searchQuery) {
      const filtered = commands.filter(cmd => 
        cmd.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cmd.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cmd.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCommands(filtered);
    } else {
      setFilteredCommands(commands);
    }
  }, [searchQuery, commandRegistry]);

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text);
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      system: '⚙️',
      thread: '💬',
      navigation: '🧭',
      create: '➕',
      search: '🔍',
      data: '📊',
      analysis: '🔬',
      ai: '🤖',
    };
    return icons[category] || '📝';
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      system: 'default',
      thread: 'blue',
      navigation: 'green',
      create: 'orange',
      search: 'purple',
      data: 'cyan',
      analysis: 'red',
      ai: 'magenta',
    };
    return colors[category] || 'default';
  };

  const renderCommandCard = (command: any) => (
    <Card
      key={command.name}
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space>
          <Text code style={{ fontSize: 14 }}>
            /{command.name}
          </Text>
          <Tag color={getCategoryColor(command.category)}>
            {getCategoryIcon(command.category)} {command.category}
          </Tag>
        </Space>
      }
      extra={
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyToClipboard(`/${command.name}`)}
        />
      }
    >
      <Paragraph style={{ marginBottom: 8 }}>
        {command.description}
      </Paragraph>
      
      <div style={{ marginBottom: 8 }}>
        <Text strong>Usage:</Text>
        <div style={{ marginTop: 4 }}>
          <Text code style={{ fontSize: 12 }}>
            {command.usage}
          </Text>
        </div>
      </div>

      {command.examples.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Text strong>Examples:</Text>
          <div style={{ marginTop: 4 }}>
            {command.examples.map((example: string, index: number) => (
              <div key={index} style={{ marginBottom: 2 }}>
                <Text code style={{ fontSize: 11 }}>
                  {example}
                </Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {command.permissions.length > 0 && (
        <div>
          <Text strong>Permissions:</Text>
          <div style={{ marginTop: 4 }}>
            {command.permissions.map((permission: string) => (
              <Tag key={permission} color="orange">
                {permission}
              </Tag>
            ))}
          </div>
        </div>
      )}
    </Card>
  );

  const renderCategoryTab = (category: string) => {
    const commands = filteredCommands.filter(cmd => cmd.category === category);
    const categoryInfo = commandRegistry.getCategories().find(cat => cat.id === category);
    
    return (
      <div>
        {categoryInfo && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
            <Title level={5} style={{ margin: 0 }}>
              {getCategoryIcon(category)} {categoryInfo.name}
            </Title>
            <Text type="secondary">{categoryInfo.description}</Text>
          </div>
        )}
        
        {commands.length > 0 ? (
          commands.map(renderCommandCard)
        ) : (
          <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
            No commands found in this category
          </div>
        )}
      </div>
    );
  };

  const renderAllCommands = () => {
    const categories = commandRegistry.getCategories();
    
    return (
      <div>
        {categories.map(category => {
          const commands = filteredCommands.filter(cmd => cmd.category === category.id);
          if (commands.length === 0) return null;
          
          return (
            <div key={category.id} style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 12 }}>
                <Title level={4} style={{ margin: 0 }}>
                  {getCategoryIcon(category.id)} {category.name}
                </Title>
                <Text type="secondary">{category.description}</Text>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
                {commands.map(renderCommandCard)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderQuickStart = () => (
    <div>
      <Title level={4}>🚀 Quick Start Guide</Title>
      
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>Getting Started</Title>
        <List
          size="small"
          dataSource={[
            'Type /help to see all available commands',
            'Use /thread create "Name" to start a new conversation',
            'Try /pin add "Artifact Name" to pin artifacts to the sidebar',
            'Use /ask "question" to get AI assistance',
          ]}
          renderItem={(item) => (
            <List.Item>
              <Text>{item}</Text>
            </List.Item>
          )}
        />
      </div>

      <Divider />

      <div style={{ marginBottom: 24 }}>
        <Title level={5}>Common Workflows</Title>
        
        <Card size="small" style={{ marginBottom: 12 }}>
          <Title level={5}>🏗️ Model Design</Title>
          <Text code>/thread create "Model Design"</Text><br />
          <Text code>/add artifact "New Entity"</Text><br />
          <Text code>/add state "Active"</Text><br />
          <Text code>/validate</Text>
        </Card>

        <Card size="small" style={{ marginBottom: 12 }}>
          <Title level={5}>🔍 Analysis</Title>
          <Text code>/find artifact "Account"</Text><br />
          <Text code>/items "Operating Checking"</Text><br />
          <Text code>/map financial_account</Text><br />
          <Text code>/check dependencies</Text>
        </Card>

        <Card size="small">
          <Title level={5}>🤖 AI Assistance</Title>
          <Text code>/ask "How do I add a new process?"</Text><br />
          <Text code>/suggest improvements</Text><br />
          <Text code>/suggest processes</Text>
        </Card>
      </div>

      <Divider />

      <div>
        <Title level={5}>💡 Tips</Title>
        <List
          size="small"
          dataSource={[
            'Use Tab to autocomplete commands',
            'Arrow keys navigate command suggestions',
            'Commands are case-insensitive',
            'Use quotes for multi-word arguments',
            'Pin frequently used artifacts for quick access',
          ]}
          renderItem={(item) => (
            <List.Item>
              <Text>{item}</Text>
            </List.Item>
          )}
        />
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <QuestionCircleOutlined />
          Command Reference
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      style={{ top: 20 }}
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search commands..."
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Quick Start" key="quickstart">
          {renderQuickStart()}
        </TabPane>
        
        <TabPane tab="All Commands" key="all">
          {renderAllCommands()}
        </TabPane>

        {commandRegistry.getCategories().map(category => (
          <TabPane
            key={category.id}
            tab={
              <Space>
                {getCategoryIcon(category.id)}
                {category.name}
                <Badge count={filteredCommands.filter(cmd => cmd.category === category.id).length} />
              </Space>
            }
          >
            {renderCategoryTab(category.id)}
          </TabPane>
        ))}
      </Tabs>
    </Modal>
  );
};
